import { FilterValue, Option } from "..";

export const FILE_INACCESSIBLE_ERROR = "file inaccessible";
export const GDRIVE = "gdrive";
export const APP_EMAIL = "App@gmail.com";

export const Display_Value_Key: string = "displayValue";

export const Video_Flavours: Option[] = [
  { label: "1080p", value: "1080p" },
  { label: "720p", value: "720p" },
  { label: "360p", value: "360p" },
  { label: "480p", value: "480p" },
];

export const Audio_Flavours: Option[] = [
  { label: "320 kbps", value: "320" },
  { label: "256 kbps", value: "256" },
  { label: "192 kbps", value: "192" },
  { label: "128 kbps", value: "128" },
  { label: "96 kbps", value: "64" },
];

export const Video_Preview_Options: Option[] = [
  { label: "Algo_1", value: "Algo_1" },
  { label: "Algo_2", value: "Algo_2" },
  { label: "Algo_3", value: "Algo_3" },
];

export const extractUrlFromEmbed = (input: string): string => {
  if (!input) return input;
  const trimmed = input.trim();

  // Already a plain URL.
  if (/^https?:\/\//i.test(trimmed)) return trimmed;

  // Look for src="..." / src='...' (iframe/embed markup).
  const srcMatch = trimmed.match(/src\s*=\s*["']([^"']+)["']/i);
  if (srcMatch?.[1]) return srcMatch[1].trim();

  // Fallback: any http(s) URL contained in the string.
  const urlMatch = trimmed.match(/https?:\/\/[^\s"'<>]+/i);
  if (urlMatch?.[0]) return urlMatch[0].trim();

  return trimmed;
};

export const findURLType = (url: string): string => {
  url = extractUrlFromEmbed(url);
  if (/^https?:\/\//.test(url)) {
    const normalizedUrl = url.toLowerCase();
    if (url.includes("drive.google.com")) {
      return "gdrive";
    }
    if (
      normalizedUrl.includes("youtube.com") ||
      normalizedUrl.includes("youtube-nocookie.com") ||
      normalizedUrl.includes("youtu.be")
    ) {
      return "youtube_download";
    }
    if (
      normalizedUrl.includes("facebook.com") ||
      normalizedUrl.includes("fb.watch")
    ) {
      return "facebook_download";
    }
    if (normalizedUrl.includes("instagram.com")) {
      return "instagram_download";
    }
    if (
      normalizedUrl.includes("twitter.com") ||
      normalizedUrl.includes("x.com")
    ) {
      return "twitter_download";
    }
    if (normalizedUrl.includes(".m3u8")) {
      return "stream";
    }
    if (
      normalizedUrl.includes("we.tl/") ||
      normalizedUrl.includes("wetransfer.com/")
    ) {
      return "wetransfer";
    }
    return "httpdownload";
  }
  if (/^(sftp|ftp):\/\//.test(url)) {
    return "ftp";
  }
  return "unknown";
};

export const constructFtpUrl = (
  ftp_url: string,
  username: string,
  password: string
) => {
  try {
    const url = new URL(ftp_url);
    if (url.protocol !== "ftp:" && url.protocol !== "sftp:") {
      throw new Error("Invalid URL");
    }
    return `${url.protocol}//${encodeURIComponent(
      username
    )}:${encodeURIComponent(password)}@${url.host}${url.pathname}`;
  } catch (error: any) {
    console.error("Error:", error.message);
    return null;
  }
};

export const getVideoIdFromUrl = (type: string, urlOrId: string): string => {
  const normalized = extractUrlFromEmbed(urlOrId);
  try {
    const url = new URL(normalized);

    if (type === "yt") {
      // youtu.be/<id>
      if (url.hostname.includes("youtu.be")) {
        return url.pathname.split("/").filter(Boolean)[0] || "";
      }
      if (
        url.hostname.includes("youtube.com") ||
        url.hostname.includes("youtube-nocookie.com")
      ) {
        // watch?v=<id>
        const vParam = url.searchParams.get("v");
        if (vParam) return vParam;
        if (url.pathname.includes("attribution_link")) {
          const u = url.searchParams.get("u");
          if (u) {
            const inner = new URLSearchParams(
              decodeURIComponent(u).split("?")[1] || ""
            );
            const innerV = inner.get("v");
            if (innerV) return innerV;
          }
        }
        const segments = url.pathname.split("/").filter(Boolean);
        const marker = segments.findIndex((s) =>
          ["embed", "shorts", "v", "live", "video"].includes(s)
        );
        if (marker !== -1 && segments[marker + 1]) {
          return segments[marker + 1];
        }
        return "";
      }
    }

    if (type === "fb" && url.hostname.includes("facebook.com")) {
      // Try to get the `v` parameter
      const videoId = url.searchParams.get("v");
      if (videoId) return videoId;

      const pathSegments = url.pathname.split("/").filter(Boolean);
      return pathSegments[pathSegments.length - 1] || "";

      // const pathSegments = url.pathname.split('/').filter(Boolean);
      // const videoIndex = pathSegments.indexOf('videos');
      // if (videoIndex !== -1 && pathSegments.length > videoIndex + 1) {
      //   return pathSegments[videoIndex + 1]; // The next segment is the video ID
      // }
    }
    if (type === "hls" && url.pathname.includes(".m3u8")) {
      return normalized;
    }
  } catch {
    return normalized;
  }

  return "";
};

export const updateSearchParamsUtil = (
  filters: Record<string, FilterValue>,
  handleSetFilter?: any,
  setSearchParams?: any
) => {
  handleSetFilter(filters);
  const params = new URLSearchParams();
  const multipleTypeList = [
    "lang",
    "category",
    "business",
    "team",
    "agency",
    "ctype",
    "ctby",
    "tags",
    "publishing_destinations",
  ];

  Object.entries(filters).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    const isMultiType = multipleTypeList.includes(
      key.replace("displayValue", "")
    );
    const relatedKey = key.endsWith("displayValue")
      ? key
      : `${key}displayValue`;

    if (
      isMultiType &&
      filters.hasOwnProperty(relatedKey) &&
      typeof filters[relatedKey] === "string"
    ) {
      filters[relatedKey] = [filters[relatedKey] as string]; // 💥 Force string to array
    }

    if (Array.isArray(value)) {
      // ✅ Clear the key first to avoid duplication

      value.forEach((v) => {
        if (typeof v === "string" || typeof v === "number") {
          params.append(key, String(v));
        }
      });
    } else if (typeof value === "string" || typeof value === "number") {
      params.set(key, String(value));
    } else if (typeof value === "object") {
      // Only serialize if it's a plain object

      try {
        // handle edge case where someone accidentally passed object instead of string/array
        if ("length" in value && typeof value !== "function") {
          // it's probably a string (iterable), convert to array
          params.append(key, String(value));
        } else {
          // safely stringify actual objects
          params.set(key, JSON.stringify(value));
        }
      } catch (err) {
        console.warn(`Could not stringify value for key ${key}`, err);
      }
    }
  });
  setSearchParams(params, { replace: true });
};

export const handleFilterChangeUtil = (
  key: string | "clear",
  value?: FilterValue,
  displayValue?: FilterValue,
  filters?: any,
  updateIsFilter?: any,
  updateIsClearFilter?: any,
  dvk?: string
) => {
  const allowedKeys = ["rows", "page"];
  let updatedFilters = { ...filters };
  if (key !== "page" && key !== "rows") {
    filters.page = 1;
    // filters.rows = 50;
    filters.rows = 30;
  }
  if (key !== "clear") {
    // store.dispatch(updateIsClearFilter(false));
    updateIsClearFilter(false);
  }

  if (key === "clear") {
    // store.dispatch(updateIsFilter(false));
    updateIsFilter(false);
    updatedFilters = {
      rows: filters.rows,
      page: filters.page,
    };
  } else if (allowedKeys.includes(key)) {
    if (value !== undefined && value !== null) {
      updatedFilters = {
        ...filters,
        [key]: value,
        // [key + dvk]: displayValue,
      };
    } else {
      const { [key]: _, ...rest } = filters;
      updatedFilters = rest;
    }
  } else {
    if (value !== undefined && value !== null) {
      updatedFilters = {
        ...filters,
        [key]: value,
        [key + dvk]: displayValue,
        rows: filters.rows,
        page: filters.page,
      };
    } else {
      const { [key]: _, [key + dvk]: __, must_not: ____, ...rest } = filters;
      updatedFilters = {
        ...rest,
        rows: filters.rows,
        page: filters.page,
      };
    }
  }

  if (updatedFilters.page === 1) {
    delete updatedFilters.page;
  }
  // if (updatedFilters.rows === 50) {
  //   delete updatedFilters.rows;
  // }
  if (updatedFilters.rows === 30) {
    delete updatedFilters.rows;
  }

  return updatedFilters;
};

interface ColumnConfig {
  key: string;
  header: string;
  getValue?: (item: any) => string;
}

export const downloadAsCSV = (
  data: any[],
  filename: string,
  columns: ColumnConfig[]
) => {
  // Create CSV header row
  const headers = columns.map((col) => col.header).join(",");

  // Create CSV rows
  const rows = data.map((item) => {
    return columns
      .map((col) => {
        const value = col.getValue ? col.getValue(item) : item[col.key];
        // Handle values that might contain commas or quotes
        const formattedValue = String(value ?? "")
          .replace(/"/g, '""') // Escape quotes
          .replace(/\n/g, " "); // Replace newlines with spaces
        return `"${formattedValue}"`;
      })
      .join(",");
  });

  // Combine headers and rows
  const csv = [headers, ...rows].join("\n");

  // Create blob and download
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");

  // Handle IE 10+ separately
  const nav = navigator as any;
  if (nav.msSaveBlob) {
    nav.msSaveBlob(blob, filename);
  } else {
    // Other browsers
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
};
